import { Controller } from "web-ui/controller"
import { interactiveSelector } from "web-ui/interactive-elements"
import * as Log from "web-ui/log"

const countPlaceholder = "%{count}"

// Connects to data-controller="multi-select"
export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "button"
  ]

  static events = {
    "change [data-multi-select-target~='selectAll']": "selectAllChanged",
    "change [data-multi-select-target~='checkbox']": "checkboxChanged",
    "click [data-multi-select-target~='checkbox']": "checkboxClicked",
    "click .multi-select-item": "itemClicked",
    "mousedown .multi-select-item": "itemMouseDown"
  }

  connect() {
    this.rangeAnchor = null
  }

  checkboxTargetConnected() {
    this.refresh()
  }

  checkboxTargetDisconnected() {
    this.refresh()
  }

  selectAllChanged(event, selectAll) {
    for (const checkbox of this.checkboxTargets) {
      checkbox.checked = selectAll.checked
    }

    this.refresh()
  }

  checkboxChanged() {
    this.refresh()
  }

  // The change event follows the click, so checkboxChanged performs the
  // refresh. - Aaron, Thu Aug 27 2026
  checkboxClicked(event, checkbox) {
    // A click focuses the checkbox as the mousedown's default action, and
    // itemMouseDown prevents that default action for a shift-click.
    // - Aaron, Fri Aug 28 2026
    checkbox.focus()

    this.updateRange(checkbox, event.shiftKey)
  }

  // A shift-click extends the text selection natively, and the browser decides
  // the extent from where the last click landed rather than from the item the
  // user shift-clicked. Preventing the default suppresses that extension, and
  // removing the ranges discards a selection an earlier drag left behind, which
  // is what a click without shift does natively. - Aaron, Fri Aug 28 2026
  itemMouseDown(event) {
    if (!event.shiftKey) return

    event.preventDefault()

    window.getSelection().removeAllRanges()
  }

  itemClicked(event, item) {
    if (event.target.closest(".multi-select-exclude")) return

    // This also matches the item's own checkbox, which checkboxClicked already
    // handled. - Aaron, Thu Aug 27 2026
    if (event.target.closest(interactiveSelector)) return

    // A drag to select text ends in a click. A shift-click leaves no selection
    // to find here, because itemMouseDown removed it. - Aaron, Thu Aug 27 2026
    if (window.getSelection().toString().trim()) return

    const checkbox = this.checkboxTargets.find((checkbox) => item.contains(checkbox))
    if (checkbox === undefined) return

    checkbox.checked = !checkbox.checked

    this.updateRange(checkbox, event.shiftKey)

    this.refresh()
  }

  updateRange(checkbox, extending) {
    if (extending && this.checkboxTargets.includes(this.rangeAnchor)) {
      this.extendRange(checkbox)

      return
    }

    this.rangeAnchor = checkbox
  }

  extendRange(checkbox) {
    const checkboxes = this.checkboxTargets

    const anchorIndex = checkboxes.indexOf(this.rangeAnchor)
    const checkboxIndex = checkboxes.indexOf(checkbox)

    const first = Math.min(anchorIndex, checkboxIndex)
    const last = Math.max(anchorIndex, checkboxIndex)

    // The anchor's state overrides the state the click just gave the clicked
    // checkbox. - Aaron, Thu Aug 27 2026
    const checked = this.rangeAnchor.checked

    for (const rangeCheckbox of checkboxes.slice(first, last + 1)) {
      rangeCheckbox.checked = checked
    }

    this.debug("Extended range", { first, last, checked })
  }

  refresh() {
    const checkboxes = this.checkboxTargets
    const selectedCount = checkboxes.filter((checkbox) => checkbox.checked).length

    this.debug("Recompute", { count: checkboxes.length, selectedCount })

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = checkboxes.length > 0 && selectedCount === checkboxes.length
      this.selectAllTarget.indeterminate = selectedCount > 0 && selectedCount < checkboxes.length
    }

    for (const button of this.buttonTargets) {
      this.updateButton(button, selectedCount)
    }
  }

  updateButton(button, selectedCount) {
    button.disabled = selectedCount === 0
    button.textContent = this.buttonLabel(button, selectedCount)

    const disabledTitle = button.dataset.multiSelectDisabledTitle

    if (button.disabled && disabledTitle) {
      button.title = disabledTitle
    } else {
      button.removeAttribute("title")
    }
  }

  buttonLabel(button, selectedCount) {
    if (selectedCount === 0) {
      return button.dataset.multiSelectLabel
    }

    if (selectedCount === 1) {
      return button.dataset.multiSelectCountLabelSingular
    }

    return button.dataset.multiSelectCountLabel.replace(countPlaceholder, selectedCount)
  }

  debug(message, data) {
    Log.debug("Multi Select - " + message, data)
  }
}
